// Import past orders from the old app's order report (Super Admin).
// 1 choose the file + dates  →  2 "Check file" (nothing is saved)  →  3 "Load orders"  →  Undo if needed.
import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";

const nf = (n) => Number(n || 0).toLocaleString("en-IN");
const ymd = (d) => d.toISOString().slice(0, 10);
const todayIST = () => new Date(Date.now() + 5.5 * 3600e3);
const yearAgoIST = () => new Date(Date.now() + 5.5 * 3600e3 - 365 * 86400e3);
const RETRIES = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// The web server in front of the app accepts only ~1 MB per request, so the file goes in pieces.
async function uploadFile(file, onProgress) {
  const { data } = await api.post("/legacy-orders/uploads", { name: file.name, size: file.size });
  const size = Number(data.chunk_size) || 700000;
  const count = Math.ceil(file.size / size);
  for (let i = 0; i < count; i += 1) {
    const slice = file.slice(i * size, Math.min(file.size, (i + 1) * size));
    for (let attempt = 0; ; attempt += 1) {
      try {
        await api.put(`/legacy-orders/uploads/${data.upload_id}/chunks/${i}`, slice, {
          headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
        });
        break;
      } catch (e) {
        const st = e?.response?.status;
        if (attempt >= RETRIES || (st && st < 500 && st !== 408 && st !== 429)) throw e;
        await sleep(1000 * 2 ** attempt);
      }
    }
    onProgress(Math.round(((i + 1) * 100) / count));
  }
  return data.upload_id;
}

function Row({ label, value, tone }) {
  const cls = tone === "bad" ? "text-rose-700" : tone === "good" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-neutral-100 last:border-0 text-sm">
      <span className="text-neutral-600">{label}</span>
      <span className={`font-semibold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}

function Summary({ s }) {
  const problems = (s.missing_distributors?.length || 0) + (s.missing_skus?.length || 0);
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-neutral-200 p-3">
        <Row label="Rows in the file" value={nf(s.file_rows)} />
        <Row label="Rows outside the chosen dates (ignored)" value={nf(s.rows_out_of_range)} />
        <Row label="Orders found in the dates" value={nf(s.orders_in_file)} />
        <Row label="Orders already in the app (will be skipped)" value={nf(s.already_loaded)} />
        <Row label="Orders that will be loaded" value={nf(s.orders_to_load)} tone="good" />
        <Row label="Product lines that will be loaded" value={nf(s.lines_to_load)} />
        <Row label="Total quantity" value={`${nf(s.total_kg)} kg`} />
        <Row label="Total value" value={`₹${nf(s.total_value)}`} />
        {s.date_min && <Row label="Order dates" value={`${s.date_min} to ${s.date_max}`} />}
      </div>
      <div className="rounded-xl border border-neutral-200 p-3">
        <Row label="Outlets already in the app" value={nf(s.retailers_found)} />
        <Row label="New outlets that will be created" value={nf(s.retailers_to_create)} />
        <Row label="Orders without a matching person (shown to admin only)" value={nf(s.orders_without_user)} tone={s.orders_without_user ? "bad" : undefined} />
        <Row label="Orders skipped: distributor not in the app" value={nf(s.orders_skipped_no_distributor)} tone={s.orders_skipped_no_distributor ? "bad" : undefined} />
        <Row label="Lines skipped: product not in the app" value={nf(s.lines_skipped_no_sku)} tone={s.lines_skipped_no_sku ? "bad" : undefined} />
        {s.lines_skipped_no_qty > 0 && <Row label="Lines skipped: no quantity" value={nf(s.lines_skipped_no_qty)} tone="bad" />}
      </div>
      {problems > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm space-y-2">
          <div className="font-semibold text-amber-900 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Not found in the app — add these first if you want them included
          </div>
          {s.missing_distributors?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-amber-800 mb-1">Distributors</div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {s.missing_distributors.map((d) => (
                  <div key={d.code} className="text-xs text-amber-900">{d.code} {d.name ? `· ${d.name}` : ""} — {nf(d.orders)} orders</div>
                ))}
              </div>
            </div>
          )}
          {s.missing_skus?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-amber-800 mb-1">Products</div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {s.missing_skus.map((d) => (
                  <div key={d.code} className="text-xs text-amber-900">{d.code} {d.name ? `· ${d.name}` : ""} — {nf(d.lines)} lines</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ImportPastOrders() {
  const [file, setFile] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => ymd(yearAgoIST()));
  const [dateTo, setDateTo] = useState(() => ymd(todayIST()));
  const [uploadPct, setUploadPct] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [job, setJob] = useState(null);          // the job being watched
  const [checked, setChecked] = useState(null);  // finished "check" job for the current file
  const [jobs, setJobs] = useState([]);
  const [confirmImport, setConfirmImport] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try { const { data } = await api.get("/legacy-orders/jobs"); setJobs(data.items || []); } catch { /* not allowed or offline */ }
  }, []);
  useEffect(() => { loadJobs(); return () => clearInterval(pollRef.current); }, [loadJobs]);

  function watch(id, onDone) {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/legacy-orders/jobs/${id}`);
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(pollRef.current);
          onDone(data);
          loadJobs();
        }
      } catch { /* keep trying */ }
    }, 1500);
  }

  async function startJob(mode, upId) {
    const { data } = await api.post("/legacy-orders/jobs", {
      upload_id: upId, mode, date_from: dateFrom, date_to: dateTo,
    });
    setJob(data);
    watch(data.id, (d) => {
      if (d.status === "failed") toast.error(d.error || "It did not work. Please try again.");
      else if (mode === "preview") setChecked(d);
      else { setChecked(null); toast.success(`${nf(d.summary?.orders_loaded)} orders loaded`); }
      setBusy(false);
    });
  }

  async function checkFile() {
    if (!file) return;
    if (dateFrom > dateTo) { toast.error("The 'from' date is after the 'to' date."); return; }
    setBusy(true); setChecked(null); setJob(null);
    try {
      let up = uploadId;
      if (!up) {
        setUploadPct(0);
        up = await uploadFile(file, setUploadPct);
        setUploadId(up);
        setUploadPct(null);
      }
      await startJob("preview", up);
    } catch (e) {
      setUploadPct(null); setBusy(false);
      toast.error(formatApiError(e));
    }
  }

  async function loadOrders() {
    setConfirmImport(false);
    setBusy(true);
    try { await startJob("import", uploadId); }
    catch (e) { setBusy(false); toast.error(formatApiError(e)); }
  }

  async function undo(j) {
    setConfirmUndo(null);
    try {
      const { data } = await api.post(`/legacy-orders/jobs/${j.id}/undo`);
      toast.success(`Removed ${nf(data.orders_removed)} orders and ${nf(data.outlets_removed)} new outlets`);
      loadJobs();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function downloadProblems(j) {
    try {
      const res = await api.get(`/legacy-orders/jobs/${j.id}/problems`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `past-orders-problems-${j.id.slice(0, 8)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const running = job && (job.status === "queued" || job.status === "running");
  const prog = job?.progress || {};
  const done = job && job.status === "done" && job.mode === "import" ? job : null;
  const s = checked?.summary;

  return (
    <div className="space-y-4 max-w-3xl p-4 sm:p-6" data-testid="import-past-orders-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Import past orders" }]}
        title="Import past orders"
        subtitle="Bring the old app's order history into this app. Nothing is saved until you press Load orders."
        testid="import-past-orders-header"
      />

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-4">
          <div>
            <Label>1. Old app order report</Label>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.tsv,.txt" className="hidden"
                     onChange={(e) => { setFile(e.target.files?.[0] || null); setUploadId(null); setChecked(null); setJob(null); }}
                     data-testid="import-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Choose file
              </Button>
              <span className="text-sm text-neutral-600 break-all">
                {file ? `${file.name} (${(file.size / 1048576).toFixed(1)} MB)` : "Excel (.xlsx) or CSV, with the old report's column names. For a big file, CSV is much faster than Excel."}
              </span>
            </div>
          </div>

          <div>
            <Label>2. Which order dates to load</Label>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <Input type="date" className="h-9 w-40" value={dateFrom} max={dateTo} onChange={(e) => { setDateFrom(e.target.value); setChecked(null); }} disabled={busy} />
              <span className="text-neutral-400 text-sm">to</span>
              <Input type="date" className="h-9 w-40" value={dateTo} min={dateFrom} onChange={(e) => { setDateTo(e.target.value); setChecked(null); }} disabled={busy} />
            </div>
            <p className="text-xs text-neutral-500 mt-1.5">
              Normally the last 1 year. If the new app already has real orders from some date, set “to” to the day before it so nothing is counted twice.
            </p>
          </div>

          <Button onClick={checkFile} disabled={!file || busy} data-testid="import-check">
            {busy && !done ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            Check file
          </Button>

          {uploadPct !== null && (
            <div className="space-y-1">
              <div className="text-xs text-neutral-600">Uploading the file… {uploadPct}%</div>
              <div className="h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full bg-[#6D2C91]" style={{ width: `${uploadPct}%` }} /></div>
            </div>
          )}
          {running && (
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3 text-sm text-neutral-700 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span>
                {prog.stage || job.stage}
                {prog.rows ? ` — ${nf(prog.rows)} rows read` : ""}
                {prog.total ? ` — ${nf(prog.done)} of ${nf(prog.total)} orders` : ""}
              </span>
            </div>
          )}
          {job?.status === "failed" && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{job.error}</div>
          )}
        </CardContent>
      </Card>

      {s && checked && (
        <Card className="border-neutral-200 rounded-2xl" data-testid="import-check-result">
          <CardContent className="p-4 space-y-4">
            <div className="font-semibold text-neutral-900">What the file contains</div>
            <Summary s={s} />
            {checked.has_problem_file && (
              <Button variant="outline" size="sm" onClick={() => downloadProblems(checked)}>
                <Download className="h-4 w-4 mr-1.5" /> Download the rows with problems ({nf(s.problem_rows)})
              </Button>
            )}
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3 text-xs text-neutral-600 space-y-1">
              <div>• Quantity (kg), order count and value are taken exactly as in the old report, so the totals above match it.</div>
              <div>• Loaded orders count towards the targets of the months they were placed in.</div>
              <div>• Orders are matched to the person by name; the rest are visible to the admin only.</div>
            </div>
            <Button disabled={busy || s.orders_to_load === 0} onClick={() => setConfirmImport(true)} data-testid="import-load">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Load {nf(s.orders_to_load)} orders
            </Button>
          </CardContent>
        </Card>
      )}

      {done && (
        <Card className="border-emerald-200 bg-emerald-50 rounded-2xl" data-testid="import-done">
          <CardContent className="p-4 text-sm text-emerald-900 space-y-1">
            <div className="font-semibold flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Done</div>
            <div>{nf(done.summary?.orders_loaded)} orders and {nf(done.summary?.lines_loaded)} product lines loaded.
              {done.summary?.retailers_created ? ` ${nf(done.summary.retailers_created)} new outlets created.` : ""}</div>
            {done.summary?.orders_skipped > 0 && <div>{nf(done.summary.orders_skipped)} orders could not be loaded.</div>}
          </CardContent>
        </Card>
      )}

      {jobs.some((j) => j.mode === "import") && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-4 space-y-2">
            <div className="font-semibold text-neutral-900">Earlier imports</div>
            <div className="max-h-72 overflow-y-auto divide-y divide-neutral-100">
              {jobs.filter((j) => j.mode === "import").map((j) => (
                <div key={j.id} className="py-2 flex items-center justify-between gap-2 flex-wrap text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-neutral-900 truncate">{j.file_name}</div>
                    <div className="text-xs text-neutral-500">
                      {(j.created_at || "").slice(0, 16).replace("T", " ")} · {j.date_from} to {j.date_to} ·{" "}
                      {j.status === "done" ? `${nf(j.summary?.orders_loaded)} orders` : j.status === "failed" ? "failed" : j.status}
                      {j.undone_at ? " · removed" : ""}
                    </div>
                  </div>
                  {j.status === "done" && !j.undone_at && (
                    <Button variant="outline" size="sm" onClick={() => setConfirmUndo(j)}>
                      <Undo2 className="h-4 w-4 mr-1.5" /> Remove this import
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmImport} onOpenChange={setConfirmImport}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Load {nf(s?.orders_to_load)} past orders?</AlertDialogTitle>
            <AlertDialogDescription>
              They will appear in reports, outlet history and colours, and count towards the targets of the months they belong to.
              {s?.retailers_to_create ? ` ${nf(s.retailers_to_create)} new outlets will be created.` : ""} You can remove this import later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction onClick={loadOrders} data-testid="import-load-confirm">Load orders</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmUndo} onOpenChange={(o) => !o && setConfirmUndo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this import?</AlertDialogTitle>
            <AlertDialogDescription>
              All orders loaded by this import ({nf(confirmUndo?.summary?.orders_loaded)}) will be deleted, and the outlets it created if they have no other orders.
              Orders placed in the app are not touched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => undo(confirmUndo)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
